import { useId, useMemo, useState, type FormEvent } from 'react';
import { convertToMl, formatMetric, packageMl } from '@/lib/metrics';
import { formatPackagePrice } from '@/lib/packageLabel';
import type { CatalogRow } from '@/lib/catalog';
import type { PriceOverride, VolumeUnit } from '@/types/data';

const COUNT_PRESETS = [1, 4, 6, 12, 18, 24, 30];
const SIZE_PRESETS_OZ = [12, 16, 19.2, 24];
const SIZE_PRESETS_ML = [50, 375, 700, 750, 1000, 1750];

export function PriceEditor({
  row,
  existing,
  onSave,
  onReset,
}: {
  row: CatalogRow;
  existing?: PriceOverride;
  onSave: (override: PriceOverride) => void;
  onReset?: () => void;
}) {
  const formId = useId();
  const [price, setPrice] = useState(
    String(existing?.packagePriceUsd ?? row.price.packagePriceUsd ?? ''),
  );
  const [count, setCount] = useState(
    String(existing?.containerCount ?? row.price.containerCount ?? 1),
  );
  const [size, setSize] = useState(
    String(
      existing
        ? existing.containerMl
        : row.price.containerMl || row.nutrition?.servingMl || 355,
    ),
  );
  const [unit, setUnit] = useState<VolumeUnit>('ml');
  const [includeTax, setIncludeTax] = useState(
    existing?.includeTaxDeposit ?? false,
  );
  const [error, setError] = useState<string | null>(null);
  const [warn, setWarn] = useState<string | null>(null);

  const totalMl = useMemo(() => {
    const c = Number(count);
    const s = Number(size);
    if (!(c > 0) || !(s > 0)) return null;
    return packageMl(c, convertToMl(s, unit === 'fl_oz' ? 'fl_oz' : unit === 'L' ? 'L' : 'ml'));
  }, [count, size, unit]);

  function handleSave(e: FormEvent) {
    e.preventDefault();
    const packagePriceUsd = Number(price);
    const containerCount = Number(count);
    const sizeNum = Number(size);
    if (!(packagePriceUsd > 0) || !(containerCount > 0) || !(sizeNum > 0)) {
      setError('Price, count, and size must be positive.');
      return;
    }
    if (!Number.isInteger(containerCount)) {
      setError('Container count must be a whole number.');
      return;
    }
    const containerMl = convertToMl(
      sizeNum,
      unit === 'fl_oz' ? 'fl_oz' : unit === 'L' ? 'L' : 'ml',
    );
    setError(null);
    if (packagePriceUsd > 400 || containerMl > 5000) {
      setWarn('Unusual package — saved anyway. Double-check before sharing.');
    } else {
      setWarn(null);
    }
    onSave({
      productId: row.product.id,
      packagePriceUsd,
      containerCount,
      containerMl,
      includeTaxDeposit: includeTax,
      updatedAt: new Date().toISOString(),
      version: 1,
    });
  }

  return (
    <form className="panel" onSubmit={handleSave} aria-labelledby={`${formId}-title`}>
      <h2 id={`${formId}-title`}>Edit package price</h2>
      <p className="muted">
        Current: {row.price.label}
        {' · '}
        {formatPackagePrice(
          row.price.packagePriceUsd,
          row.price.containerCount,
          row.price.containerMl,
          { category: row.product.category },
        )}
        {row.price.checkedAt ? ` · as of ${row.price.checkedAt.slice(0, 10)}` : ''}
      </p>
      <div className="filters-panel">
        <label>
          Package price (USD)
          <input
            className="input"
            inputMode="decimal"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            required
          />
        </label>
        <label>
          Container count
          <input
            className="input"
            inputMode="numeric"
            value={count}
            onChange={(e) => setCount(e.target.value)}
            required
          />
        </label>
        <label>
          Container size
          <input
            className="input"
            inputMode="decimal"
            value={size}
            onChange={(e) => setSize(e.target.value)}
            required
          />
        </label>
        <label>
          Size unit
          <select
            className="select"
            value={unit}
            onChange={(e) => setUnit(e.target.value as VolumeUnit)}
          >
            <option value="ml">mL</option>
            <option value="fl_oz">US fl oz</option>
            <option value="L">L</option>
          </select>
        </label>
        <label className="check-row">
          <input
            type="checkbox"
            checked={includeTax}
            onChange={(e) => setIncludeTax(e.target.checked)}
          />
          Price includes tax/deposit
        </label>
      </div>
      <div className="chip-row" style={{ marginTop: '0.75rem' }}>
        {COUNT_PRESETS.map((n) => (
          <button
            key={n}
            type="button"
            className="chip"
            onClick={() => setCount(String(n))}
          >
            {n}-pack
          </button>
        ))}
      </div>
      <div className="chip-row" style={{ marginTop: '0.45rem' }}>
        {(unit === 'fl_oz' ? SIZE_PRESETS_OZ : SIZE_PRESETS_ML).map((n) => (
          <button
            key={n}
            type="button"
            className="chip"
            onClick={() => setSize(String(n))}
          >
            {n}
            {unit === 'fl_oz' ? ' oz' : ' mL'}
          </button>
        ))}
      </div>
      <p>
        Total package volume:{' '}
        <strong>{totalMl != null ? `${formatMetric(totalMl, 1)} mL` : '—'}</strong>
      </p>
      {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}
      {warn && <p style={{ color: 'var(--accent)' }}>{warn}</p>}
      <div className="row-actions">
        <button type="submit" className="btn btn-primary">
          Save price
        </button>
        {onReset && existing && (
          <button type="button" className="btn" onClick={onReset}>
            Reset this price
          </button>
        )}
      </div>
    </form>
  );
}
